import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import useSEO from '../hooks/useSEO';
import coursesSnapshot from '../data/coursesSnapshot.json';
import { STATES } from '../utils/states';

const SNAP_COURSES = coursesSnapshot.courses || [];
const SNAP_PKGS = coursesSnapshot.packages || [];

// Build the state directory from courses + packages. A state is LIVE when it has an
// active package AND at least one published course; otherwise it's COMING SOON.
// Deterministic (registry order) so react-snap's prerender and the client's first
// render produce identical output — no hydration flash.
function buildDirectory(courses, packages) {
  const live = [], soon = [];
  for (const key of Object.keys(STATES)) {
    const s = STATES[key];
    const pkg = packages.find(p => p.state_code === s.code && p.is_active);
    const hasCourses = courses.some(c => c.state === s.code);
    if (pkg && hasCourses) {
      live.push({ code: s.code, name: s.name, slug: s.slug, hours: pkg.required_hours || s.requiredHours, price: parseFloat(pkg.package_price) });
    } else {
      soon.push({ code: s.code, name: s.name, slug: s.slug, hours: s.requiredHours });
    }
  }
  return { live, soon };
}

export default function HomePage() {
  const { addBundle } = useCart();
  const navigate = useNavigate();
  // Seed from the build-time snapshot so the directory prerenders (and first-paints)
  // with real, crawlable content. The effect below refreshes it from the live API.
  const [dir, setDir] = useState(() => buildDirectory(SNAP_COURSES, SNAP_PKGS));
  const [addingBundle, setAddingBundle] = useState(null);

  useSEO({
    title: 'Online Real Estate CE — Arizona & Kansas | Steady Education',
    // Prices come from the state config so this can't go stale when a price changes.
    description: `Approved online real estate CE for Arizona ($${STATES.arizona.packagePrice}, ${STATES.arizona.requiredHours} hrs) and Kansas ($${STATES.kansas.packagePrice}, ${STATES.kansas.requiredHours} hrs). Self-paced, instant certificates. Utah & New Jersey next.`,
    url: 'https://steadyeducation.com',
  });

  useEffect(() => {
    Promise.all([api.get('/courses'), api.get('/courses/state-packages')])
      .then(([cRes, pRes]) => {
        const courses = Array.isArray(cRes?.data?.courses) ? cRes.data.courses : SNAP_COURSES;
        const packages = Array.isArray(pRes?.data?.packages) ? pRes.data.packages : SNAP_PKGS;
        setDir(buildDirectory(courses, packages));
      })
      .catch(() => {});
  }, []);

  // Lowest per-course price across the live states, for the hero's "from $X".
  const minCoursePrice = Math.min(
    ...dir.live.map(l => STATES[l.slug]?.coursePrice).filter(Boolean)
  ).toFixed(2);

  // Holds the state code being added, so only the clicked card shows "Adding…".
  const handleBuyBundle = async (stateCode) => {
    setAddingBundle(stateCode);
    try { await addBundle(stateCode); navigate('/cart'); }
    catch (err) { alert(err.response?.data?.error || 'Could not add package'); }
    finally { setAddingBundle(null); }
  };

  return (
    <div>
      {/* Hero + state directory */}
      <section style={styles.hero}>
        <div style={styles.heroBg} />
        <div className="container" style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Renew Your <span style={styles.heroAccent}>Real Estate License</span></h1>
          <p style={styles.heroSubtitle}>Approved online continuing education, priced for the state you're licensed in. Find yours below.</p>
          <div style={styles.trustPill}>
            <span>✓ Approved in every state listed</span>
            <span style={styles.pillDivider}>|</span>
            {/* States price differently (AZ $9.99/course, KS $14.99), so quote the
                lowest per-course price across the live states rather than one state's. */}
            <span>Courses from ${minCoursePrice}</span>
            <span style={styles.pillDivider}>|</span>
            <span>24-hour access</span>
          </div>

          <div style={styles.dirHeader}>
            <h2 style={styles.dirTitle}>Choose your state</h2>
            <span style={styles.dirNote}>Hours shown are the full renewal requirement for a salesperson license.</span>
          </div>
          <div style={styles.dirGrid}>
            {dir.live.map(s => (
              <Link key={s.code} to={`/${s.slug}/courses`} style={styles.liveCard} className="state-live-card">
                <div>
                  <div style={styles.stateName}>{s.name}</div>
                  <div style={styles.stateHours}>{s.hours} hours required</div>
                </div>
                <div style={styles.statePrice}>${s.price.toFixed(2)}</div>
              </Link>
            ))}
            {dir.soon.map(s => (
              <Link key={s.code} to={`/${s.slug}`} style={styles.soonCard} className="state-soon-card">
                <div>
                  <div style={styles.stateName}>{s.name}</div>
                  <div style={styles.stateHours}>{s.hours} hours required</div>
                </div>
                <span style={styles.soonBadge}>Coming soon</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Value prop + Complete CE Package */}
      <section style={styles.section}>
        <div className="container" style={styles.valueWrap}>
          <div>
            <h2 style={styles.valueTitle}>Everything the commission asks for, nothing it doesn't</h2>
            <p style={styles.valueDesc}>Each course is built against the current state syllabus and reviewed every renewal cycle, so you're never buying hours that won't count.</p>
            <ul style={styles.valueList}>
              {['Interactive lessons with optional audio', 'Start, pause and resume anytime', 'Works on phone, tablet and desktop', 'Certificate delivered instantly'].map(v => (
                <li key={v} style={styles.valueItem}><span style={styles.valueCheck}>✓</span> {v}</li>
              ))}
            </ul>
          </div>
          {/* One compact card per live state, stacked — every live state's package is
              purchasable from the homepage, not just the first one. */}
          {dir.live.length > 0 && (
            <div style={styles.pkgStack}>
              {dir.live.map(s => (
                <div key={s.code} style={styles.pkgCard}>
                  <span style={styles.pkgTag}>★ Best value</span>
                  <h3 style={styles.pkgTitle}>{s.name} Complete CE Package</h3>
                  <div style={styles.pkgPriceRow}>
                    <span style={styles.pkgPrice}>${s.price.toFixed(2)}</span>
                    <span style={styles.pkgMeta}>{s.hours} hours</span>
                  </div>
                  <button
                    style={styles.pkgBtn}
                    onClick={() => handleBuyBundle(s.code)}
                    disabled={addingBundle !== null}
                  >
                    {addingBundle === s.code ? 'Adding…' : 'Buy the package →'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const styles = {
  hero: { position: 'relative', overflow: 'hidden', background: 'linear-gradient(135deg, #1a3a3a 0%, #1d4142 50%, #255455 100%)', padding: '72px 0 64px' },
  heroBg: { position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 72% 28%, rgba(179,158,137,0.10) 0%, transparent 60%)' },
  heroContent: { position: 'relative', maxWidth: 1120, margin: '0 auto' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', color: 'white', lineHeight: 1.12, textAlign: 'center', marginBottom: 16 },
  heroAccent: { color: '#b39e89' },
  heroSubtitle: { fontSize: 18, color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 620, margin: '0 auto 28px', lineHeight: 1.6 },
  trustPill: { display: 'flex', gap: 14, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 999, padding: '10px 24px', width: 'fit-content', maxWidth: '100%', margin: '0 auto 52px', fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 600 },
  pillDivider: { color: 'rgba(255,255,255,0.25)' },
  dirHeader: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 22, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.12)' },
  dirTitle: { fontFamily: 'var(--font-display)', fontSize: 28, color: 'white', margin: 0 },
  dirNote: { fontSize: 13, color: 'rgba(255,255,255,0.55)' },
  dirGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 },
  liveCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 14, padding: '20px 22px', textDecoration: 'none' },
  soonCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 14, padding: '20px 22px', textDecoration: 'none' },
  stateName: { fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 700, color: 'white' },
  stateHours: { fontSize: 12.5, color: 'rgba(255,255,255,0.6)', marginTop: 2 },
  statePrice: { fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: '#d4b896', whiteSpace: 'nowrap' },
  soonBadge: { fontSize: 11, fontWeight: 700, color: '#d4b896', background: 'rgba(179,158,137,0.15)', border: '1px solid rgba(179,158,137,0.3)', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' },
  section: { padding: '72px 0', background: 'white' },
  valueWrap: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'center' },
  valueTitle: { fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: 'var(--slate-900)', lineHeight: 1.2, marginBottom: 16 },
  valueDesc: { fontSize: 16, color: 'var(--color-text-muted)', lineHeight: 1.7, marginBottom: 24 },
  valueList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  valueItem: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 15.5, color: 'var(--slate-700)' },
  valueCheck: { color: '#b39e89', fontWeight: 800 },
  // Cards are compact (roughly half the old height) so every live state's package
  // stacks in the space the single Arizona card used to take.
  pkgStack: { display: 'grid', gap: 16 },
  pkgCard: { background: 'linear-gradient(135deg, #1a3a3a 0%, #234b4c 100%)', borderRadius: 16, padding: '18px 20px 20px', color: 'white' },
  pkgTag: { display: 'inline-block', background: 'rgba(179,158,137,0.2)', color: '#d4b896', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, border: '1px solid rgba(179,158,137,0.3)', marginBottom: 10 },
  pkgTitle: { fontFamily: 'var(--font-display)', fontSize: 19, color: 'white', marginBottom: 10 },
  pkgPriceRow: { display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  pkgPrice: { fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 800, color: 'white' },
  pkgMeta: { fontSize: 13, color: 'rgba(255,255,255,0.65)' },
  pkgBtn: { width: '100%', background: '#b39e89', color: '#1a3a3a', border: 'none', borderRadius: 10, padding: '11px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};
